import React, { useContext, useEffect } from 'react';
import ContentLoader from 'react-content-loader';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import IconTouch from '@/assets/icon-touch.svg?react';
import { PERSONA_LOCK_COOKIE } from '@/core/utils/rapidstor';
import { fadeOut } from '@/core/utils/ui';

import store from '@/store';
import { setResponse } from '@/store/bookingSlice';

import Button from '@/core/components/elements/button';
import Header from '@/core/components/elements/header';
import JourneyProgress from '@/core/components/journey-progress';

import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import WebhookService from '@/features/rapidstor/services/WebhookService';

import classes from '@/scss/screens/index.module.scss';

export interface PersonaType {
    _id?: string;
    label: string;
    filter: string;
    icon: string;
    text: string;
    available?: boolean;
    customerType?: 'personal' | 'business' | null;
    parameters?: { name: string; value: string }[];
    deals?: { descriptors: string[]; id: string }[];
}

export default function Index() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const startPosition = searchParams.get('startPosition');
    const {
        selectionTypes, unitList, unitListLoading, setPersona, rapidstorMainColor,
    } = useContext(JourneyContext);
    const [, setCookie, removeCookie] = useCookies([
        'persona',
        'selectedCustomerType',
        PERSONA_LOCK_COOKIE,
    ]);

    const selectionTypeDefaults: PersonaType[] = [
        {
            label: t('index:personal'),
            filter: '!Parking',
            icon: 'fa-personal',
            text: t('index:personalText'),
            customerType: 'personal',
        },
        {
            label: t('index:business'),
            filter: '!Parking',
            icon: 'fa-business',
            text: t('index:businessText'),
            customerType: 'business',
        },
    ];
    const journeySelectionTypes = selectionTypes?.length ? selectionTypes : selectionTypeDefaults;

    // Provide the name of the icon to be displayed, or the URL of the image
    const getIcon = (name?: string): React.ReactNode | undefined => {
        if (!name) {
            return undefined;
        }
        switch (name) {
            case 'fa-personal':
                return (
                    <img
                        alt="Personal"
                        className={classes.icon}
                        src="https://ik.imagekit.io/bytcx9plm/935647edb94ded8eefee2d31d1284528.png"
                    />
                );
            case 'fa-business':
                return (
                    <img
                        alt="Business"
                        className={classes.icon}
                        src="https://ik.imagekit.io/bytcx9plm/1d5c68a15515a6adf10ab84e7322a280.png"
                    />
                );
            case 'fa-moving':
                return (
                    <img
                        alt="Moving"
                        className={classes.icon}
                        src="https://ik.imagekit.io/bytcx9plm/3x6_i_o.png"
                    />
                );
            case 'fa-vehicle':
                return (
                    <img
                        alt="Vehicle"
                        className={classes.icon}
                        src="https://ik.imagekit.io/bytcx9plm/carpark.png"
                    />
                );
            default:
                return (
                    <img
                        alt={name}
                        className={classes.icon}
                        src={name}
                    />
                );
        }
    };

    const buildNextURL = (): string => {
        let goTo = '/unit-listing';
        if (startPosition) {
            goTo += `?startPosition=${startPosition}`;
        }
        return goTo;
    };

    const skipToUnitListing = () => {
        removeCookie('persona');
        // The user is making their own choice here, so drop any pre-set lock marker.
        removeCookie(PERSONA_LOCK_COOKIE, { path: '/' });
        // Clear any previously-selected persona so the unit list isn't filtered by it.
        setPersona(undefined);
        firebaseLogEvent('skip_storage_type', {});
        fadeOut();
        setTimeout(() => {
            store.dispatch(setResponse({ id: 'sTypeName.re', response: 'All Units' }));
            navigate(buildNextURL());
        }, 400);
    };

    useEffect(() => {
        if (window.rapidstor_settings.rapidstor_options.corpToken) {
            const webhookSvc = new WebhookService(location);
            webhookSvc.pageLoad(window.rapidstor_settings.rapidstor_options.corpToken);
        }
    }, [location]);

    const onClick = (type: PersonaType) => {
        // A persona chosen here is user-selected, not pinned — clear any lock marker so the user
        // can return to this screen and choose again.
        removeCookie(PERSONA_LOCK_COOKIE, { path: '/' });
        if (type?._id) {
            setPersona(type._id);
            setCookie(
                'persona',
                type._id,
                {
                    path: '/',
                    maxAge: 60 * 60 * 24,
                },
            );
            removeCookie('selectedCustomerType');
        } else {
            setCookie(
                'selectedCustomerType',
                type.customerType,
                {
                    path: '/',
                    maxAge: 60 * 60 * 24,
                },
            );
            removeCookie('persona');
            // Clear any previously-selected persona so the unit list isn't filtered by it.
            setPersona(undefined);
        }
        firebaseLogEvent('select_storage_type', {
            storage_type: type.label,
        });
        fadeOut();
        setTimeout(() => {
            navigate(buildNextURL());
        }, 400);
    };

    const cardClasses = [classes.card].filter(Boolean).join(' ');

    return (
        <>
            <div style={{
                padding: '0 12px',
            }}
            >
                <JourneyProgress mobile />
                <div>
                    <Header
                        level={1}
                        className={classes.title}
                        data-testid="storagetype-header"
                    >
                        {t('index:header_1')}
                        <span className="underline">{t('index:header_2')}</span>
                        {t('index:header_3')}
                    </Header>
                    <JourneyProgress desktop />
                    {location && unitList && !unitListLoading && selectionTypes ? (
                        <div className={classes.responseRow}>
                            {journeySelectionTypes.map((type: PersonaType) => (
                                <div
                                    className={cardClasses}
                                    onClick={() => {
                                        onClick(type);
                                    }}
                                    id={`card-${type.label}`}
                                    key={type.label}
                                >
                                    <div
                                        className={classes.cardBody}
                                        data-testid="card"
                                    >
                                        <span
                                            className={`${classes.name} font-weight-bold`}
                                        >
                                            {type.label}
                                            {' '}
                                        </span>
                                        <div className={classes.icon_container}>
                                            {getIcon(type.icon)}
                                        </div>
                                        <span className={classes.description}>
                                            {type.text}
                                        </span>
                                        <div className={classes.button_container}>
                                            <button
                                                type="button"
                                                className={classes.continue_button}
                                                onClick={() => {
                                                    onClick(type);
                                                }}
                                            >
                                                {t('cta:select')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={classes.responseRow}>
                            <div className={`${cardClasses}`}>
                                <div className={`${classes.cardBody}`}>
                                    <ContentLoader
                                        foregroundColor={rapidstorMainColor ?? location.skin.mainColour ?? '#eee'}
                                        backgroundColor="#f5f5f5"
                                        width="100%"
                                        height="100%"
                                    >
                                        <rect
                                            x="0"
                                            y="4"
                                            rx="3"
                                            ry="3"
                                            width="100%"
                                            height="24"
                                        />

                                        <rect
                                            x="0"
                                            y="40"
                                            rx="3"
                                            ry="3"
                                            width="100%"
                                            height="140px"
                                        />

                                        <rect
                                            x="calc(50% - 32px)"
                                            y="260"
                                            rx="3"
                                            ry="3"
                                            height="23px"
                                            style={{
                                                width: '64px',
                                            }}
                                            className={`${classes.button_container}`}
                                        />
                                    </ContentLoader>
                                </div>
                            </div>
                            <div className={`${cardClasses}`}>
                                <div className={`${classes.cardBody}`}>
                                    <ContentLoader
                                        foregroundColor={rapidstorMainColor ?? location.skin.mainColour ?? '#eee'}
                                        backgroundColor="#f5f5f5"
                                        width="100%"
                                        height="100%"
                                    >
                                        <rect
                                            x="0"
                                            y="4"
                                            rx="3"
                                            ry="3"
                                            width="100%"
                                            height="24"
                                        />

                                        <rect
                                            x="0"
                                            y="40"
                                            rx="3"
                                            ry="3"
                                            width="100%"
                                            height="140px"
                                        />

                                        <rect
                                            x="calc(50% - 32px)"
                                            y="260"
                                            rx="3"
                                            ry="3"
                                            height="23px"
                                            style={{
                                                width: '64px',
                                            }}
                                            className={`${classes.button_container}`}
                                        />
                                    </ContentLoader>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className={`${classes.touchIcon}`}>
                <IconTouch />
                <span className={`${classes.span}`}>{t('index:select_option')}</span>
            </div>
            <Button
                onclick={skipToUnitListing}
                type="outlineOnBackground"
                size="medium"
                extClasses={classes.skip_button}
                testId="storagetype-skip"
                key="storagetype-skip"
            >
                {t('index:skip_button_text')}
            </Button>
        </>
    );
}
